import React, { useCallback } from 'react';
import isEmpty from 'lodash/isEmpty';

import type { View } from 'common/types/view';
import type { GuidanceSummaryV2 } from 'common/types/approvals';

import { I18n } from 'common/i18n';
import AccessManager, { AccessManagerProps } from 'common/components/AccessManager';
import { MODES as ACCESS_MANAGER_MODES } from 'common/components/AccessManager/Constants';
import { defaultAccessManagerOnConfirm, PermissionsSavedPayload } from 'common/components/AccessManager/Util';
import currentUserCanTransferOwnership from 'common/views/current_user_can_transfer_ownership';

import { ActionItem } from '../ActionItem';

export interface TransferOwnershipActionItemProps {
  view: View;
  setCurrentModal: (
    modal: typeof AccessManager,

    // 'onDismiss' and 'view' are both passed in by `setCurrentModal`
    props: Omit<AccessManagerProps, 'onDismiss' | 'view'>,
  ) => void;
  approvalsGuidance: GuidanceSummaryV2;
  closeCurrentModal: () => void;
  transferOwnership: (permissionsInfo: PermissionsSavedPayload) => void;
}

export const TransferOwnershipActionItem = ({
  view,
  setCurrentModal,
  closeCurrentModal,
  transferOwnership,
  approvalsGuidance,
}: TransferOwnershipActionItemProps) => {
  const closeCurrentModalAndUpdate = useCallback(
    (updatePayload: PermissionsSavedPayload) => {
      closeCurrentModal();
      transferOwnership(updatePayload);
    },
    [closeCurrentModal, transferOwnership],
  );

  const handleClick = useCallback(() => {
    setCurrentModal(AccessManager, {
      mode: ACCESS_MANAGER_MODES.CHANGE_OWNER,
      onConfirm: defaultAccessManagerOnConfirm(closeCurrentModalAndUpdate),
    });
  }, [setCurrentModal, closeCurrentModalAndUpdate]);

  // Don't render if view is empty, locked, or user doesn't have permission
  if (isEmpty(view) || view.locked || !currentUserCanTransferOwnership(view, approvalsGuidance)) {
    return null;
  }

  return (
    <ActionItem
      label={I18n.t('shared.asset_browser.result_list_table.action_dropdown.transfer_ownership')}
      onClick={handleClick}
    />
  );
};
